import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { isLoaded } from "react-redux-firebase";
import { useAuth } from "./useAuth";
import {
  getAllowedCountries,
  getCmsRole,
  getEffectivePermissions,
  getScope,
  isCountryAllowed as isCountryAllowedFn,
  SCOPE_ALL,
} from "../../functions/permissions";

/**
 * Wat mag de ingelogde CMS-gebruiker, en welke klanten ziet hij.
 *
 * - `can(permission)` — zie functions/permissions.js voor de lijst. Effectief:
 *   rechten van de rol uit Firestore `cmsRoles` (of de standaard in code als
 *   dat document ontbreekt) plus/min de uitzonderingen op het profiel.
 * - `permissions` — de effectieve lijst.
 * - `scope` — `{ type: "all" | "countries" | "customers" | "none", ... }`.
 * - `allowedCountries` — null = geen landbeperking; anders ISO-2 codes.
 *   Alleen bij een landen-scope gevuld; bij een klantselectie null.
 * - `isScoped` — true als niet alles zichtbaar is.
 * - `loading` — profiel of rollen nog niet binnen. Wacht daarop voordat je
 *   iets verbergt of doorlaat, anders flitst het menu.
 *
 * `cmsRoles` wordt geladen in layouts/app-layout (na login). Wordt die query
 * geweigerd (regels nog niet bijgewerkt), dan is `requesting` false zonder
 * data en vallen we terug op de standaard in code — het CMS blijft werken.
 */
export const usePermissions = () => {
  const { profile, profileLoading, loading: authLoading } = useAuth();
  const roleDocsList = useSelector((state) => state.firestoreReducer.ordered.cmsRoles);
  const rolesRequesting = useSelector((state) => state.firestoreReducer.status?.requesting?.cmsRoles);

  const roleDocs = useMemo(() => {
    const map = {};
    (roleDocsList || []).forEach((doc) => {
      map[doc.id] = doc;
    });
    return map;
  }, [roleDocsList]);

  const role = useMemo(() => getCmsRole(profile), [profile]);
  const permissions = useMemo(() => getEffectivePermissions(profile, roleDocs), [profile, roleDocs]);
  const scope = useMemo(() => getScope(profile), [profile]);
  const allowedCountries = useMemo(() => getAllowedCountries(profile), [profile]);

  const can = useCallback((permission) => permissions.includes(permission), [permissions]);
  const isCountryAllowed = useCallback(
    (countryCode) => isCountryAllowedFn(allowedCountries, countryCode),
    [allowedCountries],
  );

  // Rollen zijn "klaar" als ze geladen zijn, of als de query is afgerond zonder
  // data (geweigerd). Zolang er nog nooit om gevraagd is (requesting undefined)
  // en er geen data is, wachten we — behalve zonder ingelogde CMS-rol, dan is
  // er niets te laden.
  const rolesReady = !role || isLoaded(roleDocsList) || rolesRequesting === false;

  return {
    role,
    permissions,
    roleDocs,
    can,
    scope,
    allowedCountries,
    isScoped: scope.type !== SCOPE_ALL,
    isCountryAllowed,
    loading: authLoading || profileLoading || !rolesReady,
  };
};

export default usePermissions;
